import { Address } from './address';
import { OrderLineAttribution } from './order-line-attribution';
import { Discount } from './discount';
import { TableType } from './table';
import { Translation } from './translations';

export type OrderLinePaymentStatus = 'OPEN' | 'PAID';

/** A line stays billable until a settled split has covered its quantity. */
export function isUnpaidPaymentLine(line: {
  paymentStatus?: OrderLinePaymentStatus | null;
}): boolean {
  return line.paymentStatus !== 'PAID';
}

export interface Order {
  id: string;
  menuItems: OrderMenuItem[];
  deals: OrderDeal[];
  sessionId: string;
  deliveryAddress?: Address | null;
  submissionKey?: string | null;
  status: string;
  isPaid: boolean;
  createdAt?: string;
  updatedAt?: string;
  timezoneOffsetMinutes: number;
  subtotal?: number;
  table?: {
    id: string;
    name: string;
    deviceId: string;
    tableType?: TableType;
  };
  type?: string;
  customerName?: string;
  customerPhone?: string;
  customerEmail?: string;
  confirmationEmailSentAt?: string | null;
  deliveryScheduleMode?: 'asap' | 'later' | null;
  pickupTime?: string;
  paymentMethod?: string;
  shippingCostName?: string | null;
  shippingCostAmount?: number | null;
  couponCode?: string | null;
  couponDiscountType?: 'PERCENTAGE' | 'FIXED' | 'FREE_ITEM' | 'OTHER' | null;
  couponDiscountAmount?: number | null;
}
export interface OrderMenuItem extends OrderLineAttribution {
  id?: string;
  deal?: {
    id: string;
    translations: OrderDealTranslation[];
    images?: OrderMenuItemImage[];
  };
  menuItem: {
    id: string;
    name: string;
    posId?: string;
    available: boolean;
    internalId?: string;
    category: OrderCategory;
    translations: OrderMenuItemTranslation[];
    images?: OrderMenuItemImage[];
  };
  modifiers?: (OrderMenuItemModifier | OrderMenuItemUpgrade)[];
  upgrades?: OrderMenuItemOrderUpgradeRow[];
  discounts: Discount[];
  variation_options?: OrderMenuItemVariationOption[];
  note?: string | null;
  paymentStatus?: OrderLinePaymentStatus;
  quantity: number;
  variationOptionsForInsert?: {
    optionId: string;
    optionPrice: number;
  }[];
  price: number;
  extras?: OrderMenuItemExtra[];
}

export enum OrderMenuItemModifierType {
  MODIFIER = 'MODIFIER',
  DISCOUNT = 'DISCOUNT',
  UPGRADE = 'UPGRADE',
}
export interface OrderMenuItemModifier {
  id: string;
  type: OrderMenuItemModifierType;
  /** Unit surcharge from modifier_option (per selected option count). */
  price: number;
  /** Selected count for QUANTITY-type options (`menu_item_order_modifier.quantity`); defaults to 1. */
  quantity?: number;
  option?: {
    id: string;
    translations?: OrderMenuItemVariationOptionTranslation[];
    modifier?: {
      id: string;
      translations?: OrderMenuItemVariationTranslation[];
    };
  };
}

export interface OrderMenuItemUpgrade extends OrderMenuItemModifier {
  stage: number;
}

export interface OrderMenuItemTranslation extends Translation {}

export interface OrderMenuItemOrderUpgradeRow {
  id: string;
  stage: number;
  price: number;
  upgrade?: {
    translations: Translation[];
  };
}

export interface OrderMenuItemImage {
  imageUrl: { url: string };
}

export interface OrderMenuItemVariationOption {
  id: string;
  menu_item_order_id: string;
  option: {
    id: string;
    translations: OrderMenuItemVariationOptionTranslation[];
    variation: OrderMenuItemVariation;
    variation_id: string;
  };
  variation_option_id: string;
  price: number;
}

export interface OrderMenuItemVariation {
  id: string;
  menu_item_id: string;
  translations: OrderMenuItemVariationTranslation[];
}

export interface OrderMenuItemExtra {
  extra: {
    id: string;
    translations: OrderMenuItemExtraTranslation[];
  };
  price: number;
}

export interface OrderMenuItemExtraTranslation extends Translation {}

export interface OrderCategory {
  translations: {
    id: string;
    name: string;
    languageCode: string;
  }[];
}

export interface OrderMenuItemVariationOptionTranslation {
  id: string;
  languageCode: string;
  name: string;
  variation_option_id: string;
}

export interface OrderMenuItemVariationTranslation {
  id: string;
  description: string;
  languageCode: string;
  name: string;
  variation_id: string;
}

export interface CreatedOrderAPIResponse {
  order_id: string;
  status: string;
  menu_items: {
    note: string | null;
    menuItem: {
      id: string;
    };
    quantity: number;
  }[];
}

export interface OrderDealCatalogItem {
  position?: number;
  menuItem?: OrderMenuItem['menuItem'];
}

export interface OrderDealOrderVariationRow {
  id: string;
  menu_item_id: string;
  variation_option_id: string | null;
  price: number;
  menuItem?: OrderMenuItem['menuItem'];
  option?: OrderMenuItemVariationOption['option'];
}

export interface OrderDealOrderExtraRow {
  id: string;
  menu_item_id: string;
  price: number;
  menuItem?: OrderMenuItem['menuItem'];
  extra?: OrderMenuItemExtra['extra'];
}

export interface OrderDealOrderModifierRow {
  id: string;
  menu_item_id: string;
  /** Zero-based deal line slot; defaults to 0 for legacy rows. */
  line_index?: number;
  price: number;
  /** Selected count for QUANTITY-type options; defaults to 1. */
  quantity?: number;
  menuItem?: OrderMenuItem['menuItem'];
  option?: NonNullable<OrderMenuItemModifier['option']>;
}

export interface OrderDeal extends OrderLineAttribution {
  id: string;
  deal?: {
    id: string;
    translations: OrderDealTranslation[];
    images?: OrderMenuItemImage[];
    items?: OrderDealCatalogItem[];
  };
  variations?: OrderDealOrderVariationRow[];
  extras?: OrderDealOrderExtraRow[];
  modifiers?: OrderDealOrderModifierRow[];
  menuItems: OrderMenuItem[];
  quantity: number;
  price: number;
  note: string;
  paymentStatus?: OrderLinePaymentStatus;
  dealDiscountedPrice: number;
  dealDiscountedRate: number;
}

export interface DealOrderModifier {
  id: string;
  menuItem: {
    id: string;
    name: string;
    available: boolean;
    translations: Translation[];
  };
  price: number;
}

export interface OrderDealTranslation extends Translation {}
